import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const ProfileAnalytics = ({ businessProfile, analytics }) => {
  const [timeRange, setTimeRange] = useState('30d');
  const [analyticsData, setAnalyticsData] = useState({
    profileViews: analytics?.views || 0,
    inquiries: analytics?.inquiries || 0,
    connections: analytics?.connections || 0,
    engagement: {
      totalEngagement: 0,
      averageTimeOnProfile: '2m 15s',
      returnVisitors: 0
    },
    demographics: {
      topCountries: ['United States', 'Canada', 'United Kingdom'],
      topIndustries: ['Technology', 'Government', 'Healthcare'],
      viewerRoles: ['City Official', 'Business Partner', 'Investor']
    },
    performance: {
      profileCompleteness: 85,
      seoOptimization: 78,
      responseRate: 92
    }
  });

  const [trendData, setTrendData] = useState([
    { period: 'Week 1', views: 45, inquiries: 3, connections: 1 },
    { period: 'Week 2', views: 62, inquiries: 8, connections: 2 },
    { period: 'Week 3', views: 78, inquiries: 12, connections: 4 },
    { period: 'Week 4', views: 95, inquiries: 18, connections: 7 }
  ]);

  const metricCards = [
    {
      title: 'Profile Views',
      value: analyticsData?.profileViews,
      change: '+23%',
      changeType: 'increase',
      icon: 'Eye',
      description: 'Total profile visits this month'
    },
    {
      title: 'Inquiries Received',
      value: analyticsData?.inquiries,
      change: '+45%',
      changeType: 'increase',
      icon: 'MessageSquare',
      description: 'Partnership inquiries and messages'
    },
    {
      title: 'New Connections',
      value: analyticsData?.connections,
      change: '+12%',
      changeType: 'increase',
      icon: 'Users',
      description: 'New professional connections made'
    },
    {
      title: 'Engagement Rate',
      value: '4.2%',
      change: '+8%',
      changeType: 'increase',
      icon: 'TrendingUp',
      description: 'Average engagement with your profile'
    }
  ];

  const timeRangeOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 3 months' },
    { value: '1y', label: 'Last year' }
  ];

  const getChangeColor = (changeType) => {
    return changeType === 'increase' ? 'text-green-600' : 'text-red-600';
  };

  const getChangeIcon = (changeType) => {
    return changeType === 'increase' ? 'TrendingUp' : 'TrendingDown';
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Profile Analytics</h3>
          <p className="text-sm text-gray-600">
            Track your profile performance and engagement metrics.
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            {timeRangeOptions?.map(option => (
              <option key={option?.value} value={option?.value}>
                {option?.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricCards?.map((metric, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Icon name={metric?.icon} className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <div className={`flex items-center text-sm ${getChangeColor(metric?.changeType)}`}>
                <Icon name={getChangeIcon(metric?.changeType)} className="w-4 h-4 mr-1" />
                {metric?.change}
              </div>
            </div>
            
            <div>
              <h4 className="text-2xl font-bold text-gray-900 mb-1">{metric?.value}</h4>
              <p className="text-sm font-medium text-gray-600 mb-1">{metric?.title}</p>
              <p className="text-xs text-gray-500">{metric?.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Performance Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Profile Performance */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Profile Performance</h4>
          
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Profile Completeness</span>
                <span className="text-sm font-medium text-gray-900">{analyticsData?.performance?.profileCompleteness}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${analyticsData?.performance?.profileCompleteness}%` }}
                ></div>
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">SEO Optimization</span>
                <span className="text-sm font-medium text-gray-900">{analyticsData?.performance?.seoOptimization}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${analyticsData?.performance?.seoOptimization}%` }}
                ></div>
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Response Rate</span>
                <span className="text-sm font-medium text-gray-900">{analyticsData?.performance?.responseRate}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${analyticsData?.performance?.responseRate}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Engagement Insights */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Engagement Insights</h4>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center">
                <Icon name="Clock" className="w-5 h-5 text-gray-400 mr-3" />
                <span className="text-sm text-gray-600">Avg. Time on Profile</span>
              </div>
              <span className="text-sm font-medium text-gray-900">{analyticsData?.engagement?.averageTimeOnProfile}</span>
            </div>
            
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center">
                <Icon name="RotateCcw" className="w-5 h-5 text-gray-400 mr-3" />
                <span className="text-sm text-gray-600">Return Visitors</span>
              </div>
              <span className="text-sm font-medium text-gray-900">{analyticsData?.engagement?.returnVisitors}%</span>
            </div>
            
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center">
                <Icon name="Heart" className="w-5 h-5 text-gray-400 mr-3" />
                <span className="text-sm text-gray-600">Profile Saves</span>
              </div>
              <span className="text-sm font-medium text-gray-900">24</span>
            </div>
          </div>
        </div>
      </div>

      {/* Trend Analysis */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Trend Analysis</h4>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Period</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Views</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Inquiries</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Connections</th>
              </tr>
            </thead>
            <tbody>
              {trendData?.map((data, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 text-sm text-gray-900">{data?.period}</td>
                  <td className="py-3 px-4 text-sm text-gray-900 text-right">{data?.views}</td>
                  <td className="py-3 px-4 text-sm text-gray-900 text-right">{data?.inquiries}</td>
                  <td className="py-3 px-4 text-sm text-gray-900 text-right">{data?.connections}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Demographics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Top Countries</h4>
          <div className="space-y-3">
            {analyticsData?.demographics?.topCountries?.map((country, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{country}</span>
                <div className="flex items-center">
                  <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${85 - (index * 20)}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-500">{85 - (index * 20)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Top Industries</h4>
          <div className="space-y-3">
            {analyticsData?.demographics?.topIndustries?.map((industry, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{industry}</span>
                <div className="flex items-center">
                  <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${75 - (index * 15)}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-500">{75 - (index * 15)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Viewer Roles</h4>
          <div className="space-y-3">
            {analyticsData?.demographics?.viewerRoles?.map((role, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{role}</span>
                <div className="flex items-center">
                  <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                    <div
                      className="bg-purple-600 h-2 rounded-full"
                      style={{ width: `${65 - (index * 10)}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-500">{65 - (index * 10)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-yellow-50 rounded-lg p-6">
        <div className="flex items-start">
          <Icon name="Lightbulb" className="w-6 h-6 text-yellow-600 mt-1 mr-3" />
          <div>
            <h4 className="text-md font-medium text-yellow-900 mb-2">Optimization Recommendations</h4>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• Add more detailed case studies to increase engagement time</li>
              <li>• Update your services list to improve SEO optimization</li>
              <li>• Consider adding team member profiles to build trust</li>
              <li>• Respond to inquiries within 24 hours to maintain high response rate</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileAnalytics;